import { FC, useEffect, useRef, useState } from 'react';

import { useActionInput } from '@bonusly/common-action-input';
import { ModalBody, ModalBodyProps } from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

// TODO: This would make a better custom hook.
// so if someone does a window change mid-activity, or if they drag a file out of the browser
// before they technically leave the element in the visible window, we need to kill the drag event
// so that it doesn't trigger a drop event on the wrong element
const dragTargets = new Set<HTMLElement>();

const dispatchSyntheticDragLeave = (target: HTMLElement) => {
	// Synthetic dragleave should not bubble to window, or it can re-trigger global dragleave handlers recursively.
	target.dispatchEvent(new DragEvent('dragleave', { bubbles: false, cancelable: true }));
};

// kill the drag events in progress, if a more "global" event like blur happens.
window.addEventListener('blur', () => {
	dragTargets.forEach((target) => {
		dispatchSyntheticDragLeave(target);
	});
});
window.addEventListener('dragleave', () => {
	dragTargets.forEach((target) => {
		dispatchSyntheticDragLeave(target);
	});
});

export const GiveModalContentBody: FC<ModalBodyProps> = (props) => {
	const { onUpload } = useActionInput();
	const contentRef = useRef<HTMLDivElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const company = useCurrentCompany();
	const enabledVideoUpload = !company?.disable_video_uploads;
	const enabledImageUpload = !company?.disable_image_uploads;
	const enabledAttachmentUpload = enabledVideoUpload && enabledImageUpload;
	const enabledDragDrop = enabledVideoUpload || enabledImageUpload;

	useEffect(() => {
		const dropTarget = contentRef.current;
		if (dropTarget) dragTargets.add(dropTarget);

		function shouldHandle(e: DragEvent) {
			const files = e.dataTransfer?.files;
			const file = files?.[0];
			const types = file?.type ? [file.type] : e.dataTransfer?.types;
			const isAttachment = types?.some((type) => type.startsWith('image/') || type.startsWith('video/'));
			const isVideo = types?.some((type) => type.startsWith('video/'));
			const isImage = types?.some((type) => type.startsWith('image/'));
			if (!isAttachment) return false;
			if (enabledImageUpload && isImage) return true;
			if (enabledVideoUpload && isVideo) return true;
			return false;
		}

		function dragOver(e: DragEvent) {
			if (!enabledDragDrop) return; // only drop has file access
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(true);
			if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
		}

		function dragLeave(e: DragEvent) {
			setIsDragging(false);
			if (!enabledDragDrop) return; // only drop has file access
			e.preventDefault();
			e.stopPropagation();
		}

		function dropFile(e: DragEvent) {
			setIsDragging(false);
			if (!shouldHandle(e)) return;
			e.preventDefault();
			e.stopPropagation();
			const file = e.dataTransfer?.files?.[0];
			if (file) {
				onUpload(file);
			}
		}

		function click(e: MouseEvent) {
			if (isDragging) {
				e.preventDefault();
				e.stopPropagation();
				setIsDragging(false);
			}
		}

		dropTarget?.addEventListener('dragover', dragOver);
		dropTarget?.addEventListener('drop', dropFile);
		dropTarget?.addEventListener('dragleave', dragLeave);
		dropTarget?.addEventListener('click', click);
		return () => {
			if (dropTarget) dragTargets.delete(dropTarget);
			dropTarget?.removeEventListener('dragover', dragOver);
			dropTarget?.removeEventListener('drop', dropFile);
			dropTarget?.removeEventListener('dragleave', dragLeave);
			dropTarget?.removeEventListener('click', click);
		};
	}, [isDragging, onUpload, enabledDragDrop, enabledImageUpload, enabledVideoUpload]);

	return (
		<ModalBody {...props} ref={contentRef} position="relative">
			<div
				style={{
					border: '4px dashed #CCC',
					borderRadius: '4px',
					display: enabledDragDrop && isDragging ? 'flex' : 'none',
					alignItems: 'center',
					justifyContent: 'center',
					position: 'absolute',
					top: '4px',
					left: '4px',
					right: '4px',
					bottom: '4px',
					zIndex: 1000,
					backgroundColor: 'rgba(255, 255, 255, 0.9)',
					color: '#999',
					fontSize: '24px',
					fontWeight: 'bold',
				}}
			>
				{enabledAttachmentUpload ? 'DRAG ATTACHMENT HERE' : enabledVideoUpload ? 'DROP VIDEO HERE' : enabledImageUpload ? 'DROP IMAGE HERE' : ''}
			</div>
			{props.children}
		</ModalBody>
	);
};
